import type React from 'react';

export interface FieldErrorState {
    error: boolean;
    message?: string;
    /**
     * Reports an edit to the resolver only — the control keeps painting whatever value the
     * consumer gives it — so a rejection clears as the field is fixed, not at the next submit.
     */
    onFieldChange?: (value: unknown) => void;
    onFieldBlur?: () => void;
    /** What this form means by "nothing chosen" — see `emptyValueOf`. */
    emptyValue: unknown;
    /** The value the form holds for the field, which is what a bound control must paint. */
    value: unknown;
}

/**
 * The shape a cleared field should report. A form seeds an empty field with `''`, `null` or
 * nothing at all, and expects that same shape back: handed a different one it never sees the
 * field return to its default and stays dirty for good. A field seeded with a value states no
 * empty shape of its own, so `''` is reported rather than a guess.
 */
export const emptyValueOf = (initialValue: unknown): unknown =>
    initialValue === '' || initialValue === null || initialValue === undefined
        ? initialValue
        : '';

export type FormItemChildren = React.ReactNode | ((state: FieldErrorState) => React.ReactNode);

export const NO_FIELD_ERROR: FieldErrorState = {
    error: false,
    message: undefined,
    emptyValue: '',
    value: undefined,
};

export const renderFormItemChildren = (
    children: FormItemChildren,
    state: FieldErrorState,
): React.ReactNode => (typeof children === 'function' ? children(state) : children);
